import React, { useState, useMemo } from 'react';
import { Tag } from 'antd-v5';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { useMerchantTags } from '../../../hooks/query/useEvents';
import { useMerchantStores } from '../../../hooks/query/useStores';
import TagSelectionModal from '../../Routes/Configuration/Events/components/TagSelectionModal';
import StoresFilterModal from './StoresFilterModal';
import ContactMethodFilterModal from './ContactMethodFilterModal';
import LastContactedFilterModal from './LastContactedFilterModal';
import './AudienceBuilder.scss';

const CONTACT_METHOD_LABELS = { EMAIL: 'Email', SMS: 'SMS' };

const getLastContactedChipLabel = lc => {
  if (!lc) return '';
  switch (lc.mode) {
    case 'never':
      return 'Never contacted';
    case 'range': {
      const parts = [];
      if (lc.from) parts.push(dayjs(lc.from).format('MM/DD/YYYY'));
      if (lc.to) parts.push(dayjs(lc.to).format('MM/DD/YYYY'));
      return parts.join(' – ');
    }
    case 'any':
      return 'Has been contacted';
    default:
      return '';
  }
};

const AudienceBuilder = ({
  configuration,
  onChange,
  audienceCount,
  isCountLoading,
  onViewAudience,
  showViewAudienceButton,
}) => {
  const [activeModal, setActiveModal] = useState(null);

  const { data: tagsData, isLoading: isLoadingTags } = useMerchantTags();
  const { data: storesData } = useMerchantStores();

  const tags = tagsData?.results || [];
  const stores = storesData?.results || storesData?.data || [];

  const selectedTagIds = configuration?.tags?.included || [];
  const excludedTagIds = configuration?.tags?.excluded || [];
  const includedStoreIds = configuration?.stores?.included || [];
  const contactMethods = configuration?.contactMethods || [];
  const lastContacted = configuration?.lastContacted || null;

  // Lookup maps
  const tagMap = useMemo(() => {
    const map = {};
    tags.forEach(t => { map[t.id] = t; });
    return map;
  }, [tags]);

  const storeMap = useMemo(() => {
    const map = {};
    stores.forEach(s => { map[s.id] = s; });
    return map;
  }, [stores]);

  // --- Tag handlers ---
  const handleRemoveTag = tagId => {
    const newIds = selectedTagIds.filter(id => id !== tagId);
    onChange({ ...configuration, tags: { ...configuration.tags, included: newIds } });
  };

  const handleTagModalConfirm = newSelectedIds => {
    onChange({ ...configuration, tags: { ...configuration.tags, included: newSelectedIds } });
  };

  // --- Excluded tag handlers ---
  const handleRemoveExcludedTag = tagId => {
    const newIds = excludedTagIds.filter(id => id !== tagId);
    onChange({ ...configuration, tags: { ...configuration.tags, excluded: newIds } });
  };

  const handleExcludedTagModalConfirm = newIds => {
    onChange({ ...configuration, tags: { ...configuration.tags, excluded: newIds } });
  };

  // --- Store handlers ---
  const handleRemoveStore = storeId => {
    const newIds = includedStoreIds.filter(id => id !== storeId);
    const updated = { ...configuration };
    if (newIds.length > 0) {
      updated.stores = { included: newIds };
    } else {
      delete updated.stores;
    }
    onChange(updated);
  };

  const handleStoresApply = newIds => {
    const updated = { ...configuration };
    if (newIds.length > 0) {
      updated.stores = { included: newIds };
    } else {
      delete updated.stores;
    }
    onChange(updated);
  };

  // --- Contact method handlers ---
  const handleRemoveContactMethod = method => {
    const newMethods = contactMethods.filter(m => m !== method);
    const updated = { ...configuration };
    if (newMethods.length > 0) {
      updated.contactMethods = newMethods;
    } else {
      delete updated.contactMethods;
    }
    onChange(updated);
  };

  const handleContactMethodsApply = newMethods => {
    const updated = { ...configuration };
    if (newMethods.length > 0) {
      updated.contactMethods = newMethods;
    } else {
      delete updated.contactMethods;
    }
    onChange(updated);
  };

  // --- Last Contacted handlers ---
  const handleLastContactedApply = val => {
    const updated = { ...configuration };
    if (val) {
      updated.lastContacted = val;
    } else {
      delete updated.lastContacted;
    }
    onChange(updated);
  };

  const handleRemoveLastContacted = () => {
    const updated = { ...configuration };
    delete updated.lastContacted;
    onChange(updated);
  };

  return (
    <div className="audience-builder">
      {/* All filter rows always visible */}
      <div className="audience-builder__chip-groups">
        <div className="audience-builder__chip-group">
          <button type="button" className="audience-builder__chip-label audience-builder__chip-label--clickable" onClick={() => setActiveModal('tags')}>
            + Tags
          </button>
          <div className="audience-builder__chip-list">
            {selectedTagIds.map(id => {
              const tag = tagMap[id];
              return (
                <Tag key={id} closable onClose={() => handleRemoveTag(id)} className="audience-builder__tag">
                  {tag?.name || `Tag ${id}`}
                </Tag>
              );
            })}
          </div>
        </div>

        <div className="audience-builder__chip-group">
          <button type="button" className="audience-builder__chip-label audience-builder__chip-label--clickable" onClick={() => setActiveModal('excludeTags')}>
            + Exclude Tags
          </button>
          <div className="audience-builder__chip-list">
            {excludedTagIds.map(id => {
              const tag = tagMap[id];
              return (
                <Tag key={id} closable onClose={() => handleRemoveExcludedTag(id)} className="audience-builder__tag audience-builder__tag--excluded">
                  {tag?.name || `Tag ${id}`}
                </Tag>
              );
            })}
          </div>
        </div>

        <div className="audience-builder__chip-group">
          <button type="button" className="audience-builder__chip-label audience-builder__chip-label--clickable" onClick={() => setActiveModal('stores')}>
            + Stores
          </button>
          <div className="audience-builder__chip-list">
            {includedStoreIds.map(id => {
              const store = storeMap[id];
              return (
                <Tag key={id} closable onClose={() => handleRemoveStore(id)} className="audience-builder__tag">
                  {store?.name || `Store ${id}`}
                </Tag>
              );
            })}
          </div>
        </div>

        <div className="audience-builder__chip-group">
          <button type="button" className="audience-builder__chip-label audience-builder__chip-label--clickable" onClick={() => setActiveModal('contactMethods')}>
            + Contact Methods
          </button>
          <div className="audience-builder__chip-list">
            {contactMethods.map(method => (
              <Tag key={method} closable onClose={() => handleRemoveContactMethod(method)} className="audience-builder__tag">
                {CONTACT_METHOD_LABELS[method] || method}
              </Tag>
            ))}
          </div>
        </div>

        <div className="audience-builder__chip-group">
          <button type="button" className="audience-builder__chip-label audience-builder__chip-label--clickable" onClick={() => setActiveModal('lastContacted')}>
            + Last Contacted
          </button>
          <div className="audience-builder__chip-list">
            {lastContacted && (
              <Tag closable onClose={handleRemoveLastContacted} className="audience-builder__tag">
                {getLastContactedChipLabel(lastContacted)}
              </Tag>
            )}
          </div>
        </div>
      </div>

      {/* Filter modals */}
      <TagSelectionModal
        open={activeModal === 'tags'}
        onClose={() => setActiveModal(null)}
        onConfirm={handleTagModalConfirm}
        tags={tags}
        selectedTagIds={selectedTagIds}
        isLoading={isLoadingTags}
        excludeTagIds={excludedTagIds}
      />

      <TagSelectionModal
        open={activeModal === 'excludeTags'}
        onClose={() => setActiveModal(null)}
        onConfirm={handleExcludedTagModalConfirm}
        tags={tags}
        selectedTagIds={excludedTagIds}
        isLoading={isLoadingTags}
        title="Exclude Tags"
        subtitle="Clients with these tags will be removed from your audience."
        excludeTagIds={selectedTagIds}
      />

      <StoresFilterModal
        open={activeModal === 'stores'}
        onClose={() => setActiveModal(null)}
        stores={stores}
        includedStoreIds={includedStoreIds}
        onApply={handleStoresApply}
      />

      <ContactMethodFilterModal
        open={activeModal === 'contactMethods'}
        onClose={() => setActiveModal(null)}
        value={contactMethods}
        onApply={handleContactMethodsApply}
      />

      <LastContactedFilterModal
        open={activeModal === 'lastContacted'}
        onClose={() => setActiveModal(null)}
        value={lastContacted}
        onApply={handleLastContactedApply}
      />
    </div>
  );
};

AudienceBuilder.propTypes = {
  configuration: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  audienceCount: PropTypes.number,
  isCountLoading: PropTypes.bool,
  onViewAudience: PropTypes.func,
  showViewAudienceButton: PropTypes.bool,
};

AudienceBuilder.defaultProps = {
  audienceCount: 0,
  isCountLoading: false,
  onViewAudience: null,
  showViewAudienceButton: false,
};

export default AudienceBuilder;
